/**
 * CoverageAuditGrid.tsx
 *
 * Data-integrity matrix for /coverage/audit.
 * Jurisdictions (rows) × pipeline stages (columns).
 * Read-only. No re-queue actions.
 */
import React, { useEffect, useState } from 'react';
import { useSupabaseAuth } from '@/lib/context/AuthContext';
import {
  useCoverageAudit,
  type CoverageAuditRow,
  type CoverageAuditTableStatus,
} from '@/hooks/useCoverageAudit';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Minus,
  HelpCircle,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

type StageKey =
  | 'total_artifacts'
  | 'extracted_done'
  | 'classified'
  | 'chunked'
  | 'embedded_searchable'
  | 'governing_body_populated'
  | 'attendee_rows'
  | 'vote_breakdown_rows';

interface StageDef {
  key: StageKey;
  label: string;
  shortLabel: string;
  headerTooltip: string;
  dependsOnTable?: keyof CoverageAuditTableStatus;
  comparableToTotal?: boolean;
}

// ── Stage definitions ─────────────────────────────────────────────────────────

const STAGES: StageDef[] = [
  {
    key: 'total_artifacts',
    label: 'Artifacts scoped',
    shortLabel: 'Artifacts',
    headerTooltip:
      'Total artifacts assigned to this jurisdiction (artifacts.jurisdiction_id).',
    comparableToTotal: false,
  },
  {
    key: 'extracted_done',
    label: 'Extracted (extracted_texts.status = done)',
    shortLabel: 'Extracted',
    headerTooltip:
      'Artifacts whose extracted_texts row has status = done. Partial means some artifacts were never successfully extracted.',
    comparableToTotal: true,
  },
  {
    key: 'classified',
    label: 'Classified (artifacts.classified_at IS NOT NULL)',
    shortLabel: 'Classified',
    headerTooltip:
      'Artifacts with a non-null classified_at timestamp. Partial means some extracted artifacts were not classified.',
    comparableToTotal: true,
  },
  {
    key: 'chunked',
    label: 'Chunked (≥1 chunk)',
    shortLabel: 'Chunked',
    headerTooltip:
      'Artifacts that have at least one chunk row. Partial means some classified artifacts were never chunked.',
    comparableToTotal: true,
  },
  {
    key: 'embedded_searchable',
    label:
      'Embedded / Searchable (≥1 chunk with embedding, correct jurisdiction)',
    shortLabel: 'Searchable',
    headerTooltip:
      'Artifacts with at least one chunk whose embedding is populated and whose jurisdiction_id matches the artifact.',
    comparableToTotal: true,
  },
  {
    key: 'governing_body_populated',
    label: 'Governing Body populated',
    shortLabel: 'Gov. Body',
    headerTooltip:
      'Artifacts with a non-null governing_body value. Partial means some artifacts lack a governing body.',
    comparableToTotal: true,
  },
  {
    key: 'attendee_rows',
    label: 'Attendee rows (artifact_attendees)',
    shortLabel: 'Attendees',
    headerTooltip:
      'Rows in artifact_attendees joined to artifacts in this jurisdiction. Missing means the table does not exist.',
    dependsOnTable: 'attendees_table_exists',
    comparableToTotal: true,
  },
  {
    key: 'vote_breakdown_rows',
    label: 'Vote Breakdown rows (artifact_vote_breakdown)',
    shortLabel: 'Votes',
    headerTooltip:
      'Rows in artifact_vote_breakdown joined to artifacts in this jurisdiction. Missing means the table does not exist.',
    dependsOnTable: 'vote_breakdown_table_exists',
    comparableToTotal: true,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hrs = Math.floor(mins / 60);
  if (diff < 60_000) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function classNames(...classes: (string | false | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

type CellState = 'missing' | 'empty' | 'ok' | 'partial' | 'gap';

function getCellState(
  stage: StageDef,
  row: CoverageAuditRow,
  tableStatus: CoverageAuditTableStatus
): CellState {
  if (stage.dependsOnTable && !tableStatus[stage.dependsOnTable]) {
    return 'missing';
  }

  const count = row[stage.key];
  const total = row.total_artifacts;

  if (stage.key === 'total_artifacts') {
    return total > 0 ? 'ok' : 'empty';
  }

  if (count === 0) {
    return 'empty';
  }

  if (stage.comparableToTotal && total > 0 && count < total) {
    return 'partial';
  }

  return 'ok';
}

function cellClasses(state: CellState): string {
  const base =
    'rounded-md px-2 py-1 text-center min-w-[4.5rem] border transition-colors text-sm';
  switch (state) {
    case 'missing':
      return classNames(base, 'bg-gray-100 border-gray-300 text-gray-500');
    case 'empty':
      return classNames(base, 'bg-white border-gray-200 text-gray-500');
    case 'partial':
      return classNames(base, 'bg-amber-50 border-amber-300 text-amber-900');
    case 'gap':
      return classNames(base, 'bg-red-50 border-red-300 text-red-900');
    case 'ok':
    default:
      return classNames(
        base,
        'bg-emerald-50 border-emerald-300 text-emerald-900'
      );
  }
}

function cellTooltip(
  stage: StageDef,
  row: CoverageAuditRow,
  state: CellState
): string {
  const count = row[stage.key];
  const total = row.total_artifacts;

  if (state === 'missing') {
    return `${stage.label}: table does not exist`;
  }

  if (state === 'empty') {
    if (stage.key === 'total_artifacts') {
      return 'No artifacts scoped to this jurisdiction';
    }
    return `${stage.label}: 0 rows (stage may not have run or found nothing)`;
  }

  if (state === 'partial') {
    const gap = total - count;
    return `${stage.label}: ${count.toLocaleString()} / ${total.toLocaleString()}, ${gap.toLocaleString()} gap`;
  }

  return `${stage.label}: ${count.toLocaleString()} / ${total.toLocaleString()}`;
}

function CellContent({
  stage,
  row,
  tableStatus,
}: {
  stage: StageDef;
  row: CoverageAuditRow;
  tableStatus: CoverageAuditTableStatus;
}) {
  const state = getCellState(stage, row, tableStatus);
  const count = row[stage.key];

  return (
    <div className={cellClasses(state)} title={cellTooltip(stage, row, state)}>
      <div className="flex items-center justify-center gap-1">
        {state === 'missing' && <Minus className="w-3.5 h-3.5" />}
        {state === 'empty' &&
          count === 0 &&
          stage.key !== 'total_artifacts' && (
            <HelpCircle className="w-3 h-3 opacity-70" />
          )}
        {state === 'ok' && <CheckCircle2 className="w-3 h-3 opacity-80" />}
        {state === 'partial' && <AlertTriangle className="w-3 h-3" />}
        <span className="text-xs font-semibold tabular-nums">
          {state === 'missing' ? '—' : count.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function CrossCuttingRow({
  label,
  count,
  warn,
  detail,
}: {
  label: string;
  count: number;
  warn?: boolean;
  detail?: string;
}) {
  return (
    <div
      className={classNames(
        'flex items-center justify-between rounded-xl border px-4 py-3',
        warn
          ? 'bg-red-50 border-red-300 text-red-900'
          : 'bg-white border-gray-200 text-gray-900'
      )}
    >
      <div>
        <p className="text-sm font-semibold">{label}</p>
        {detail && <p className="text-xs text-gray-500 mt-0.5">{detail}</p>}
      </div>
      <span
        className={classNames(
          'text-lg font-bold tabular-nums',
          warn ? 'text-red-700' : 'text-gray-900'
        )}
      >
        {count.toLocaleString()}
      </span>
    </div>
  );
}

function IntegrityIndicator({
  label,
  count,
  ok,
  detail,
}: {
  label: string;
  count: number;
  ok: boolean;
  detail: string;
}) {
  return (
    <div
      className={classNames(
        'flex items-center gap-3 rounded-xl border px-4 py-3',
        ok
          ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
          : 'bg-amber-50 border-amber-300 text-amber-900'
      )}
      title={detail}
    >
      {ok ? (
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
      ) : (
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-sm font-semibold">{label}</p>
        <p className="text-xs text-gray-500 truncate">{detail}</p>
      </div>
      <span
        className={classNames(
          'ml-auto text-base font-bold tabular-nums',
          ok ? 'text-emerald-700' : 'text-amber-700'
        )}
      >
        {count.toLocaleString()}
      </span>
    </div>
  );
}

// ── Main grid ─────────────────────────────────────────────────────────────────

export const CoverageAuditGrid: React.FC = () => {
  const { isLoaded: authLoaded } = useSupabaseAuth();
  const { data, loading, error, lastRefreshed, refresh } = useCoverageAudit();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoaded) return;
    void refresh();
  }, [authLoaded, refresh]);

  const jurisdictions = data?.jurisdictions ?? [];
  const crossCutting = data?.crossCutting;
  const tableStatus = data?.tableStatus;

  return (
    <div className="space-y-5">
      {/* ── Top bar: refresh ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
        <span className="text-xs text-gray-400">
          {lastRefreshed ? (
            <>Refreshed {fmtAgo(lastRefreshed.toISOString())}</>
          ) : (
            <>Not yet refreshed</>
          )}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refresh()}
          disabled={loading}
          className="border-gray-300 text-gray-700 bg-white hover:bg-gray-50 font-medium gap-1"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          Refresh
        </Button>
      </div>

      {/* ── Error state ── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-300 text-red-800 rounded-xl p-4 text-sm font-medium">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-red-600" />
          {error}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-red-700 hover:text-red-900 hover:bg-red-100"
            onClick={() => void refresh()}
          >
            Retry
          </Button>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && jurisdictions.length === 0 && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 bg-gray-100 rounded-lg animate-pulse border border-gray-200"
            />
          ))}
        </div>
      )}

      {/* ── Matrix ── */}
      {jurisdictions.length > 0 && tableStatus && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden text-gray-900">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 border-b-2 border-gray-300 hover:bg-gray-100 pointer-events-none">
                  <TableHead className="min-w-[12rem] text-gray-800 font-bold text-xs uppercase tracking-wide sticky left-0 bg-gray-100 z-10">
                    Jurisdiction
                  </TableHead>
                  {STAGES.map((stage) => (
                    <TableHead
                      key={stage.key}
                      className="text-center text-gray-800 font-bold text-xs uppercase tracking-wide min-w-[6rem] pointer-events-auto"
                      title={stage.headerTooltip}
                    >
                      <span className="inline-flex items-center justify-center gap-1 cursor-help normal-case tracking-normal">
                        {stage.shortLabel}
                        <HelpCircle className="w-3 h-3 text-gray-400" />
                      </span>
                    </TableHead>
                  ))}
                  <TableHead
                    className="text-center text-gray-800 font-bold text-xs uppercase tracking-wide min-w-[6.5rem] pointer-events-auto"
                    title="Artifacts scoped but not searchable"
                  >
                    <span className="inline-flex items-center justify-center gap-1 cursor-help normal-case tracking-normal">
                      Searchable Gap
                      <HelpCircle className="w-3 h-3 text-gray-400" />
                    </span>
                  </TableHead>
                  <TableHead
                    className="text-center text-gray-800 font-bold text-xs uppercase tracking-wide min-w-[6.5rem] pointer-events-auto"
                    title="Chunks whose jurisdiction_id differs from parent artifact"
                  >
                    <span className="inline-flex items-center justify-center gap-1 cursor-help normal-case tracking-normal">
                      Jur. Mismatch
                      <HelpCircle className="w-3 h-3 text-gray-400" />
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jurisdictions.map((row) => {
                  const hasGap = row.scoped_searchable_gap > 0;
                  const hasMismatch = row.chunk_jurisdiction_mismatch > 0;
                  const isHovered = hoveredRow === row.jurisdiction_id;

                  return (
                    <TableRow
                      key={row.jurisdiction_id}
                      className={classNames(
                        'border-b border-gray-100 transition-colors',
                        isHovered ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'
                      )}
                      onMouseEnter={() => setHoveredRow(row.jurisdiction_id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <TableCell
                        className={classNames(
                          'sticky left-0 z-10',
                          isHovered ? 'bg-slate-50' : 'bg-white'
                        )}
                      >
                        <div>
                          <p className="font-semibold text-sm text-gray-900">
                            {row.jurisdiction_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {row.jurisdiction_level}
                            {row.jurisdiction_path
                              ? ` · ${row.jurisdiction_path}`
                              : ''}
                          </p>
                        </div>
                      </TableCell>
                      {STAGES.map((stage) => (
                        <TableCell key={stage.key} className="text-center">
                          <CellContent
                            stage={stage}
                            row={row}
                            tableStatus={tableStatus}
                          />
                        </TableCell>
                      ))}
                      <TableCell className="text-center">
                        <div
                          className={classNames(
                            'rounded-md px-2 py-1 text-center min-w-[4.5rem] border text-sm font-semibold tabular-nums',
                            hasGap
                              ? 'bg-amber-50 border-amber-300 text-amber-900'
                              : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                          )}
                          title={
                            hasGap
                              ? `${row.scoped_searchable_gap.toLocaleString()} scoped artifacts have no embedded, correctly-jurisdictioned chunk`
                              : 'All scoped artifacts are searchable'
                          }
                        >
                          {row.scoped_searchable_gap.toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div
                          className={classNames(
                            'rounded-md px-2 py-1 text-center min-w-[4.5rem] border text-sm font-semibold tabular-nums',
                            hasMismatch
                              ? 'bg-red-50 border-red-300 text-red-900'
                              : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                          )}
                          title={
                            hasMismatch
                              ? `${row.chunk_jurisdiction_mismatch.toLocaleString()} chunks have a jurisdiction_id that does not match their parent artifact`
                              : 'All chunks agree with their parent artifact'
                          }
                        >
                          {row.chunk_jurisdiction_mismatch.toLocaleString()}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && jurisdictions.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-gray-500 font-medium">No jurisdictions found.</p>
        </div>
      )}

      {/* ── Cross-cutting integrity checks ── */}
      {crossCutting && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">
            Cross-cutting integrity checks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <CrossCuttingRow
              label="Orphaned artifacts"
              count={crossCutting.orphan_artifacts}
              warn={crossCutting.orphan_artifacts > 0}
              detail="jurisdiction_id IS NULL — invisible to scoped queries"
            />
            <CrossCuttingRow
              label="Orphaned chunks"
              count={crossCutting.orphan_chunks}
              warn={crossCutting.orphan_chunks > 0}
              detail="jurisdiction_id IS NULL — never returned by match_chunks"
            />
            <CrossCuttingRow
              label="Orphaned attendee rows"
              count={crossCutting.orphan_attendees}
              warn={crossCutting.orphan_attendees > 0}
              detail="parent artifact has no jurisdiction"
            />
            <CrossCuttingRow
              label="Phantom workspace scopes"
              count={crossCutting.phantom_workspaces}
              warn={crossCutting.phantom_workspaces > 0}
              detail="references a jurisdiction_id that does not exist"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <IntegrityIndicator
              label="Scoped ≠ searchable"
              count={jurisdictions.reduce(
                (sum, r) => sum + r.scoped_searchable_gap,
                0
              )}
              ok={jurisdictions.every((r) => r.scoped_searchable_gap === 0)}
              detail="Artifacts scoped to a jurisdiction but not retrievable via match_chunks"
            />
            <IntegrityIndicator
              label="Chunk-jurisdiction consistency"
              count={jurisdictions.reduce(
                (sum, r) => sum + r.chunk_jurisdiction_mismatch,
                0
              )}
              ok={jurisdictions.every(
                (r) => r.chunk_jurisdiction_mismatch === 0
              )}
              detail="Chunks whose jurisdiction_id differs from their parent artifact (should be 0)"
            />
          </div>
        </div>
      )}

      {/* ── Legend ── */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-emerald-50 border border-emerald-300" />
          Has data
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-amber-50 border border-amber-300" />
          Partial / gap
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-white border border-gray-200" />
          0 rows (stage may not have run)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-gray-100 border border-gray-300" />
          Table missing / never ran
        </span>
      </div>
    </div>
  );
};
